// ═══════════════════════════════════════════════════════════════════════════
// grainRegime.js — the CURRENT-REGIME lens, structurally separate from the
// long-term contract-life percentile (futuresContractHistory.js). Kevin,
// 2026-08-18 "GRAIN MARKET INTELLIGENCE UI" item 1: "Preserve the long-term
// contract-life percentile... Add a separate CURRENT-REGIME lens. Do not
// define regime as an arbitrary fixed 24-month window. Build a transparent
// statistical regime classifier using only defensible market features
// already available or computable from futures history/curve... If the
// current data are insufficient for a defensible regime classification,
// return UNKNOWN rather than inventing one. Long-term percentile and
// regime-relative percentile must remain separate and visible; never
// collapse them into one opaque score."
//
// THREE DIMENSIONS, each independently UNKNOWN-safe, each built from data
// this codebase ALREADY computes for other purposes — no new data source,
// no invented cutoff beyond conventions already in use elsewhere in this repo:
//
//  1. VOLATILITY REGIME — SELF-REFERENTIAL, not compared against an
//     invented "normal" number. Computes a rolling median-absolute-daily-
//     move series across THIS CONTRACT'S OWN life-to-date bars (the exact
//     statistic src/grainTargets.js's grainContractTech() already uses for
//     `absMove`, just as a time series instead of one latest reading), then
//     asks "where does TODAY's rolling reading sit within THIS CONTRACT'S
//     OWN historical distribution of that same statistic" — a genuine,
//     reproducible percentile, distinct from tc.pctile (which is a PRICE
//     percentile, not a volatility one). <30 usable rolling points ->
//     unknown, mirroring grainContractTech's own `s.length<30` minimum.
//
//  2. CURVE STRUCTURE — carry (contango, deferred priced above nearby — the
//     normal carrying-charge market) vs. inverted (backwardation, deferred
//     priced below nearby — tight nearby supply/demand). Computed directly
//     from two REAL, already-fetched contract settles (the same points
//     grainCurve.js's buildForwardCurve() already produces) — no new fetch,
//     no invented spread threshold: any real difference in sign is reported,
//     'flat' only on an exact tie.
//
//  3. TREND REGIME — reuses grainTargetLadder()'s own up/down-vs-100-day-MA
//     (falling back to the 50-day) definition verbatim, so this file adds no
//     second trend rule beside the one the target ladder already relies on.
//
// classifyMarketRegime() composes these three into ONE qualitative label for
// display — never collapsed into a numeric score (Kevin: "no black-box
// composite SELL SCORE"). scoreRegimeRelativePercentile() is the SEPARATE,
// genuinely regime-CONDITIONED cross-year percentile: the SAME comparable
// population futuresContractHistory.js's scoreContractAtLifePoint() builds
// (imported, not re-derived), narrowed to the contract-years whose OWN
// measurable local volatility at this life point falls in the same
// EMPIRICAL tercile as today's — never a fixed magnitude cutoff. Given the
// contract-life archive fixtures on file today are one-row-per-year sparse
// (docs/futures-history-import-spec.md), this conditioned percentile will
// usually and honestly read `available:false` until a denser (daily) import
// lands — see tools/check-grain-regime.js for a densely-fixtured case
// proving the mechanism itself is correct when the data supports it.
//
// PURE — no fetch, no React, no Supabase.
// ═══════════════════════════════════════════════════════════════════════════

import { opportunityCategory } from "./grainCurve";
import { filterComparableFuturesHistory } from "./futuresContractHistory";

// ── volCategory — same 67th/33rd tercile convention opportunityCategory()
// already uses for price percentile (documented there as reused, not a new
// scale) — applied here to a VOLATILITY percentile instead of a price one.
export function volCategory(pctile) {
  if (pctile == null) return "unknown";
  const p = pctile * 100;
  if (p >= 67) return "elevated";
  if (p <= 33) return "quiet";
  return "normal";
}

// ── rollingAbsMoveSeries — one point per day (after the first `window` bars),
// each the MEDIAN absolute day-over-day % move over the trailing `window`
// trading days ending there. Same statistic grainContractTech's `absMove`
// computes (median absolute daily move as a fraction of price), just kept as
// a series instead of collapsed to one latest reading, so a later reading
// can be placed within its own history's distribution.
export function rollingAbsMoveSeries(rows, window = 20) {
  const s = (rows || [])
    .filter((r) => r && r.close != null && r.bar_date)
    .map((r) => ({ d: String(r.bar_date), c: Number(r.close) }))
    .filter((r) => Number.isFinite(r.c) && r.c > 0)
    .sort((a, b) => (a.d < b.d ? -1 : a.d > b.d ? 1 : 0));
  if (s.length < window + 5) return [];
  const dailyAbsPct = [];
  for (let i = 1; i < s.length; i++) {
    dailyAbsPct.push(Math.abs(s[i].c - s[i - 1].c) / (s[i - 1].c || s[i].c));
  }
  const series = [];
  for (let i = window - 1; i < dailyAbsPct.length; i++) {
    const win = dailyAbsPct.slice(i - window + 1, i + 1).slice().sort((a, b) => a - b);
    const median = win[Math.floor(win.length / 2)];
    series.push({ date: s[i + 1].d, value: median });
  }
  return series;
}

// ── classifyVolatilityRegime — self-referential: is TODAY's rolling
// median-abs-move HIGH or LOW relative to THIS CONTRACT'S OWN full life-to-
// date distribution of that same statistic. rows: same shape
// grainContractTech(rows) already consumes ({bar_date, close, high}).
export function classifyVolatilityRegime(rows, window = 20, minPoints = 30) {
  const series = rollingAbsMoveSeries(rows, window);
  if (series.length < minPoints) {
    return { state: "unknown", reason: `fewer than ${minPoints} rolling ${window}-day volatility readings on file (have ${series.length})`, n: series.length };
  }
  const current = series[series.length - 1].value;
  const atOrBelow = series.filter((p) => p.value <= current).length;
  const pctile = atOrBelow / series.length;
  return {
    state: "known",
    pctile,
    category: volCategory(pctile),
    current,
    n: series.length,
    asOf: series[series.length - 1].date,
  };
}

// ── classifyCurveStructure — carry vs. inverted, from two REAL already-
// fetched contract settles (no new fetch). Zero invented threshold: any real
// sign difference is reported; 'flat' only on an exact numeric tie.
export function classifyCurveStructure({ nearbySettle, deferredSettle } = {}) {
  const near = Number(nearbySettle), def = Number(deferredSettle);
  if (!Number.isFinite(near) || !Number.isFinite(def) || near <= 0) {
    return { state: "unknown", spread: null, spreadPct: null };
  }
  const spread = def - near;
  const spreadPct = spread / near;
  const state = spread > 0 ? "carry" : spread < 0 ? "inverted" : "flat";
  return { state, spread: +spread.toFixed(4), spreadPct: +spreadPct.toFixed(4) };
}

// ── classifyTrendRegime — grainTargetLadder()'s own up/down-vs-100-day-MA
// (falling back to 50-day) rule, verbatim — not a second trend definition.
export function classifyTrendRegime({ last, ma50, ma100 } = {}) {
  const trendMA = ma100 != null ? ma100 : ma50;
  if (last == null || trendMA == null) return { state: "unknown" };
  return { state: last >= trendMA ? "uptrend" : "downtrend", last, trendMA };
}

// ── classifyMarketRegime — composes the three dimensions above into ONE
// qualitative label. Each dimension degrades independently to 'unknown';
// the composed label only mentions dimensions that ARE known, and reads
// "UNKNOWN REGIME" only when all three are unknown. This is a LABEL, not a
// score — nothing here multiplies/weights the dimensions together.
export function classifyMarketRegime({ rows = [], tc = null, curveNearbySettle = null, curveDeferredSettle = null, window = 20, minPoints = 30 } = {}) {
  const vol = classifyVolatilityRegime(rows, window, minPoints);
  const curve = classifyCurveStructure({ nearbySettle: curveNearbySettle, deferredSettle: curveDeferredSettle });
  const trend = tc ? classifyTrendRegime({ last: tc.last, ma50: tc.ma50, ma100: tc.ma100 }) : { state: "unknown" };

  const parts = [];
  if (vol.state === "known") parts.push(`${vol.category.toUpperCase()} VOL`);
  if (curve.state !== "unknown") parts.push(curve.state.toUpperCase());
  if (trend.state !== "unknown") parts.push(trend.state.toUpperCase());
  const label = parts.length ? parts.join(" · ") : "UNKNOWN REGIME";

  return { vol, curve, trend, label };
}

// ── yearlyLocalVol — a COARSE local-volatility proxy for one historical
// contract-year at approximately the target's own point in contract life:
// the absolute % move between the two archive rows (for that crop+month+
// year) closest in date to each other near the target's offset window. This
// is a deliberately simple, reproducible measure — NOT a rolling series like
// classifyVolatilityRegime above, because the cross-year archive
// (futuresContractHistory.js) is not guaranteed daily density. Returns null
// (never a fabricated proxy) when fewer than 2 usable rows exist for that
// year within the window.
function yearlyLocalVol({ crop, contractMonth, contractYear, history = [] }) {
  // Reuses contractLifeOffsetDays (via filterComparableFuturesHistory's own
  // logic would exclude this exact year, so filtered inline instead — this
  // year's own rows are exactly what we want, never excluded).
  const cropKey = String(crop || "").trim().toLowerCase();
  const rowsThisYear = (history || []).filter((r) => {
    if (!r || Number(r.contractYear) !== Number(contractYear)) return false;
    if (String(r.crop || "").trim().toLowerCase() !== cropKey) return false;
    if (String(r.contractMonth || "").toUpperCase() !== String(contractMonth || "").toUpperCase()) return false;
    return true;
  });
  if (rowsThisYear.length < 2) return null;
  const sorted = rowsThisYear.slice().sort((a, b) => (a.obsDate < b.obsDate ? -1 : a.obsDate > b.obsDate ? 1 : 0));
  const a = sorted[0], b = sorted[sorted.length - 1];
  if (!(a.settle > 0)) return null;
  return Math.abs(b.settle - a.settle) / a.settle;
}

// ── scoreRegimeRelativePercentile — the SAME comparable cross-year
// population scoreContractAtLifePoint() uses, narrowed to years whose OWN
// yearlyLocalVol falls in the same EMPIRICAL tercile as the target's current
// volatility reading (targetVolCategory — pass classifyVolatilityRegime()'s
// `.category`, computed from the LIVE board's own daily bars, which ARE
// available today even though the deep cross-year archive is not). Tercile
// boundaries are computed FROM the comparable set's own yearlyLocalVol
// values — never a fixed magnitude. Years whose local vol can't be computed
// (fewer than 2 archive rows that year) are excluded from the CONDITIONED
// subset but still count toward the base comparable population reported.
export function scoreRegimeRelativePercentile({
  crop, contractMonth, targetContractYear, targetOffsetDays, targetSettle, targetVolCategory,
  history = [], windowDays = 15, minSamples = 5, minContractYears = 3,
}) {
  if (targetOffsetDays == null || targetSettle == null || !Number.isFinite(targetSettle)) {
    return { available: false, reason: "no target observation to score", sampleSize: 0, yearsRepresented: 0 };
  }
  if (!targetVolCategory || targetVolCategory === "unknown") {
    return { available: false, reason: "today's volatility regime is unknown — cannot condition the cross-year comparison on it", sampleSize: 0, yearsRepresented: 0 };
  }
  const comparable = filterComparableFuturesHistory({ crop, contractMonth, targetContractYear, targetOffsetDays, history, windowDays });
  const years = [...new Set(comparable.map((r) => Number(r.contractYear)))];

  const yearVols = years
    .map((y) => ({ year: y, vol: yearlyLocalVol({ crop, contractMonth, contractYear: y, history }) }))
    .filter((yv) => yv.vol != null);

  if (yearVols.length < 3) {
    return {
      available: false,
      reason: `only ${yearVols.length} historical contract year(s) have enough archive density to compute a local volatility regime (need >= 3 to establish empirical tercile boundaries)`,
      sampleSize: comparable.length, yearsRepresented: years.length,
    };
  }
  const sortedVols = yearVols.map((yv) => yv.vol).slice().sort((a, b) => a - b);
  const q = (p) => sortedVols[Math.min(sortedVols.length - 1, Math.max(0, Math.floor(p * (sortedVols.length - 1))))];
  const lowCut = q(0.33), highCut = q(0.67);
  const categoryOf = (v) => (v <= lowCut ? "quiet" : v >= highCut ? "elevated" : "normal");

  const matchingYears = new Set(yearVols.filter((yv) => categoryOf(yv.vol) === targetVolCategory).map((yv) => yv.year));
  const conditioned = comparable.filter((r) => matchingYears.has(Number(r.contractYear)));

  if (conditioned.length < minSamples || matchingYears.size < Math.min(minContractYears, 3)) {
    return {
      available: false,
      reason: `only ${conditioned.length} observation(s) across ${matchingYears.size} contract year(s) share today's "${targetVolCategory}" volatility regime (need ${minSamples} samples across at least ${Math.min(minContractYears, 3)} matching years)`,
      sampleSize: conditioned.length, yearsRepresented: matchingYears.size,
      baseSampleSize: comparable.length, baseYearsRepresented: years.length,
    };
  }

  const atOrBelow = conditioned.filter((r) => Number(r.settle) <= targetSettle).length;
  const percentile = Math.round((atOrBelow / conditioned.length) * 100);
  return {
    available: true, percentile,
    sampleSize: conditioned.length, yearsRepresented: matchingYears.size,
    baseSampleSize: comparable.length, baseYearsRepresented: years.length,
    volRegime: targetVolCategory, comparableYears: [...matchingYears].sort((a, b) => a - b),
  };
}

// ── buildRegimeRelativeGauge — gauge-shaped wrapper, same shape convention
// buildContractLifeFuturesGauge() already uses, so a caller can render either
// with one component. Kept a SEPARATE gauge object from the long-term one —
// never merged into one number (Kevin: "must remain separate and visible").
export function buildRegimeRelativeGauge(score, contractLabel = null) {
  if (!score || !score.available) {
    return {
      state: "unknown", fillPct: null,
      label: "insufficient regime-matched history to score this contract",
      reason: (score && score.reason) || "no score computed",
      sampleSize: (score && score.sampleSize) || 0,
      yearsRepresented: (score && score.yearsRepresented) || 0,
      contractLabel,
    };
  }
  const cat = opportunityCategory(score.percentile / 100);
  return {
    state: "known", fillPct: score.percentile, category: cat,
    label: `${cat.toUpperCase()} · ${score.percentile}th percentile (regime-matched: ${score.volRegime} vol, n=${score.sampleSize} · ${score.yearsRepresented} yrs)`,
    sampleSize: score.sampleSize, yearsRepresented: score.yearsRepresented,
    volRegime: score.volRegime, comparableYears: score.comparableYears || [],
    contractLabel,
  };
}
